import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import React, { useEffect, useRef, useState } from "react";

function VariantTabBar({ tabs, activeTab, onSelect, onReorder, onRename }) {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [draftName, setDraftName] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editingIndex === null) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editingIndex]);

  const handleDragStart = (index) => {
    if (editingIndex !== null) return;
    setDragIndex(index);
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (overIndex !== index) {
      setOverIndex(index);
    }
  };

  const handleDrop = (event, index) => {
    event.preventDefault();
    if (dragIndex !== null && dragIndex !== index) {
      onReorder(dragIndex, index);
    }
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const startEditing = (index, variant) => {
    setEditingIndex(index);
    setDraftName(variant);
    onSelect(String(index));
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setDraftName("");
  };

  const commitEditing = () => {
    if (editingIndex === null) return;
    const currentName = tabs[editingIndex];
    const nextName = draftName.trim();

    if (!nextName || nextName === currentName) {
      cancelEditing();
      return;
    }

    const ok = onRename(editingIndex, nextName);
    if (ok !== false) {
      cancelEditing();
    }
  };

  return (
    <div className="vf-variant-tabbar" role="tablist" aria-label="Vehicle variants">
      {tabs.map((variant, index) => {
        const isActive = String(index) === String(activeTab);
        const isEditing = editingIndex === index;

        return (
          <div
            key={`${variant}-${index}`}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            className={[
              "vf-variant-tab",
              isActive ? "is-active" : "",
              isEditing ? "is-editing" : "",
              dragIndex === index ? "is-dragging" : "",
              overIndex === index && dragIndex !== index ? "is-drop-target" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => {
              if (!isEditing) onSelect(String(index));
            }}
            onDoubleClick={(event) => {
              event.preventDefault();
              startEditing(index, variant);
            }}
            onDragOver={(event) => handleDragOver(event, index)}
            onDrop={(event) => handleDrop(event, index)}
            onKeyDown={(event) => {
              if (isEditing) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect(String(index));
              }
            }}
          >
            <button
              type="button"
              className="vf-variant-tab-drag"
              draggable={!isEditing}
              aria-label={`Drag to reorder ${variant}`}
              onClick={(event) => event.stopPropagation()}
              onDragStart={(event) => {
                event.stopPropagation();
                handleDragStart(index);
              }}
              onDragEnd={handleDragEnd}
            >
              <DragIndicatorIcon fontSize="small" />
            </button>

            {isEditing ? (
              <div
                className="vf-variant-tab-edit"
                onClick={(event) => event.stopPropagation()}
              >
                <input
                  ref={inputRef}
                  className="vf-variant-tab-input"
                  value={draftName}
                  aria-label="Edit variant name"
                  onChange={(event) => setDraftName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      commitEditing();
                    } else if (event.key === "Escape") {
                      event.preventDefault();
                      cancelEditing();
                    }
                  }}
                  onBlur={commitEditing}
                />
                <button
                  type="button"
                  className="vf-variant-tab-action is-save"
                  aria-label="Save variant name"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={commitEditing}
                >
                  <CheckOutlinedIcon fontSize="small" />
                </button>
                <button
                  type="button"
                  className="vf-variant-tab-action is-cancel"
                  aria-label="Cancel rename"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={cancelEditing}
                >
                  <CloseOutlinedIcon fontSize="small" />
                </button>
              </div>
            ) : (
              <>
                <span className="vf-variant-tab-label">{variant}</span>
                <button
                  type="button"
                  className="vf-variant-tab-edit-btn"
                  aria-label={`Edit ${variant}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    startEditing(index, variant);
                  }}
                >
                  <EditOutlinedIcon fontSize="inherit" />
                </button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default VariantTabBar;
